import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../config";
import {
  Income,
  IncomeUpdateRequest,
  IncomeCreateRequest,
} from "../../../../api/src/types";
import { prepareHeaders } from "../utils";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const incomesTagType = "incomes" as const;

export const incomesApi = createApi({
  reducerPath: "incomesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [incomesTagType],
  endpoints: (builder) => ({
    getIncome: builder.query<Income, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/incomes/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [{ type: incomesTagType, id: query.id }],
    }),
    getIncomesByUserId: builder.query<Income[], BasePathParams>({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/incomes`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((income) => ({
          type: incomesTagType,
          id: income.id,
        })) ?? []),
        { type: incomesTagType, id: "LIST" },
      ],
    }),

    updateIncome: builder.mutation<
      Income,
      BasePathParams & { id: string; data: IncomeUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/incomes/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [{ type: incomesTagType, id }],
    }),

    createIncome: builder.mutation<
      Income,
      BasePathParams & { data: IncomeCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/incomes`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              incomesApi.util.updateQueryData(
                "getIncomesByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: Income[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query create income error", error);
        }
      },
    }),
    deleteIncome: builder.mutation<Income, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/incomes/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { userId, docId, id },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;
        dispatch(
          incomesApi.util.updateQueryData(
            "getIncomesByUserId",
            { userId, docId },
            (draft: Income[]) => {
              return draft.filter((item) => item.id !== id);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetIncomeQuery,
  useGetIncomesByUserIdQuery,
  useUpdateIncomeMutation,
  useCreateIncomeMutation,
  useDeleteIncomeMutation,
} = incomesApi;
